import {
  ReactNode, useCallback, useMemo, useState
} from 'react';
import Dropdown from '../form-inputs/inputDropdown';
import { createEndSnapshotOptions } from '../../utils/helpers';
import type { OptionType } from '../../types/forms.type';
import Button from '../buttons/baseButton';
import { useModal } from '../modalProvider';
import CreateCohortForm from './createCohortForm';
import { FrontendFilterValue } from '../../types/filters.type';
import PostValidHouseholds from '../../services/adapters/validHouseholds';
import { VerifiedHouseholdReferences } from '../../types/forms.type';
import customIcons from '../../utils/icons';
import InputCreate from '../form-inputs/inputCreateSelect';
import CheckHouseholdsButton from './checkHouseholds';
import { DelimiterRules } from '../../utils/pasteValueHandler';

interface HouseholdReferencesScopeProps {
  householdReferences: FrontendFilterValue[];
  onHouseholdReferencesChange: (references: FrontendFilterValue[]) => void;
  snapshots: OptionType[];
  selectedSnapshots: { start: string; end: string };
  onSnapshotsChange: (snapshotId: { start: string, end: string }) => void;
  children?: ReactNode;
}

export default function HouseholdReferencesScope({
  householdReferences, onHouseholdReferencesChange, snapshots, selectedSnapshots, onSnapshotsChange, children,
}: HouseholdReferencesScopeProps) {
  const endSnapshotOptions = useMemo(
    () => createEndSnapshotOptions(selectedSnapshots.start, snapshots),
    [selectedSnapshots.start, snapshots],
  );
  const { openSimpleModal, closeModal } = useModal();
  const [formSubmitted, setFormSubmitted] = useState(0);
  const [validatedHouseholds, setValidatedHouseholds] = useState<VerifiedHouseholdReferences>({ validHouseholds: [], invalidHouseholds: [] });
  const [householdCheckError, setHouseholdCheckError] = useState<string | null>(null);

  const getValidHouseholdReferences = useCallback(async (references: FrontendFilterValue[]) => {
    setHouseholdCheckError(null);
    const getValidHouseholdsAdapter = new PostValidHouseholds();
    const response = await getValidHouseholdsAdapter.adaptResponse(
      {
        selectedHouseholds: references.map((ref) => ref.displayName),
        startSnapshot: selectedSnapshots.start,
        endSnapshot: selectedSnapshots.end,
        allHouseholds: false,
      },
    );
    setValidatedHouseholds(response);
    const inputFormat = response.validHouseholds.map((household) => ({ displayName: household, id: household }));
    onHouseholdReferencesChange(inputFormat);
    if (response.invalidHouseholds.length > 0) {
      setHouseholdCheckError(`The following household references were not found and removed: ${response.invalidHouseholds.join(', ')}`);
    } else {
      setHouseholdCheckError('All references are valid');
    }
    setTimeout(() => setHouseholdCheckError(null), 5000);
    return response;
  }, [onHouseholdReferencesChange, selectedSnapshots.start, selectedSnapshots.end]);

  const onSave = useCallback(async (references: FrontendFilterValue[]) => {
    const response = await getValidHouseholdReferences(references);
    if (response) {
      openSimpleModal({
        isOpen: true,
        title: 'Create Filter Template',
        children: <CreateCohortForm
          snapshots={selectedSnapshots}
          households={response.validHouseholds}
          onClose={() => { setFormSubmitted(formSubmitted + 1); closeModal(); }}
        />,
        onClose: closeModal,
      });
    }
  }, [openSimpleModal, closeModal, formSubmitted, selectedSnapshots, getValidHouseholdReferences]);

  return (
    <div className="flex flex-col lg:flex-row lg:flex-wrap gap-6">
      <div className="flex flex-col lg:flex-row gap-4 lg:items-end">
        <InputCreate
          value={householdReferences}
          setValue={onHouseholdReferencesChange}
          label="Household references"
          labelId="paste-household-references"
          placeholder="Type or paste references and press enter"
          delimiterRules={DelimiterRules.SpacesAreDelimiter}
        />

        <div id="campaign-start-snapshot" className="flex flex-col">
          <Dropdown
            id="outcome-tracker-start-snapshot-dropdown"
            prefix="From"
            label="Snapshots"
            placement="bottom"
            options={snapshots}
            selected={selectedSnapshots.start}
            onChange={(event, value) => onSnapshotsChange({ ...selectedSnapshots, start: value?.value ?? '' })}
          />
        </div>
        <Dropdown
          id="outcome-tracker-end-snapshot-dropdown"
          prefix="To"
          label=""
          placement="bottom"
          placeholder={`${endSnapshotOptions.length > 0 ? 'Select...' : 'Start snapshot must not be latest'}`}
          options={endSnapshotOptions}
          selected={selectedSnapshots.end}
          onChange={(event, value) => onSnapshotsChange({ ...selectedSnapshots, end: value?.value ?? '' })}
        />
      </div>
      <div className="flex items-end">
        {children}
        {
          selectedSnapshots.start && selectedSnapshots.end && householdReferences.length > 0 && (
            <Button
              id="save-households"
              label="Save"
              onClick={() => onSave(householdReferences)}
              icon={customIcons.save}
              className="ml-4 button-radio border border-pie-chart-slice2 text-secondary hover:bg-gray-100"
            />
          )
        }
        <CheckHouseholdsButton
          householdReferences={householdReferences}
          startSnapshot={selectedSnapshots.start}
          endSnapshot={selectedSnapshots.end}
          getValidHouseholds={getValidHouseholdReferences}
        />
      </div>
      {householdCheckError
      && (
      <div
        className={`${validatedHouseholds.invalidHouseholds.length > 0 ? 'text-pipstatus-error' : 'text-pipstatus-success'} text-xs mt-1 self-end`}
      >
        {householdCheckError}
      </div>
      )}
    </div>
  );
}
